import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { brand } from '@/utils/brand'
import { getLambdaEndpoint } from '@/utils/urlHelpers'

const GET_COUNTRIES_QUERY = gql`
  query getCountries($input: GetCountriesInput!) {
    getCountries(input: $input) {
      code
      countryCode
      name
      publicShippable
      shipEnabled
    }
  }
`

interface CountriesQueryResponse {
  getCountries: [Country]
}

const GET_COUNTRY_QUERY = gql`
  query getCountry($input: GetCountryInput!) {
    getCountry(input: $input) {
      countryId
      code
      countryCode
      name
      shipEnabled
      publicShippable
      countryConfig {
        optionalFields
        disabledFields
        holidaysList
        minimumTransitTime
        europeanUnionPadding
        customsInvoiceType
        customPhoneNumber
        showImporterOfRecord
      }
      states {
        name
        code
      }
    }
  }
`

export type CountryConfig = {
  optionalFields?: string[]
  disabledFields?: string[]
  holidaysList?: string
  minimumTransitTime?: number
  europeanUnionPadding?: number
  customsInvoiceType?: string
  customPhoneNumber?: string
  showImporterOfRecord?: boolean
  countryId?: string
}

export interface State {
  code: string
  name: string
}

export interface Country {
  postalCodeFormats?: string[] // Array of strings
  incorrectPostalCodeNotification?: boolean
  name: string
  code: string
  shipEnabled?: boolean
  publicShippable?: boolean
  hasLandingPage?: boolean
  importFormsEnabled?: boolean
  customConfigEnabled?: boolean
  paperlessTradeEnabled?: boolean
  travelerCoHotelEnabled?: boolean
  internationalEmail?: boolean
  noteEnabled?: boolean
  permanentImport?: boolean
  europeanUnion?: boolean
  notification?: boolean
  strictCarrierPreference?: boolean
  specialServicesImport?: boolean
  specialServicesExport?: boolean
  internationalEmailOrigin?: boolean
  domesticallyShippable?: boolean
  notificationo?: boolean // this is a second field in country config data, not a typo
  brand?: string
  countryCode: string
  countryId?: string // ID in GraphQL is usually mapped to string in TypeScript
  countryConfig?: CountryConfig // Assuming CountryConfig is defined elsewhere
  states?: State[]
}

interface CountryQueryResponse {
  getCountry: Country
}

export const useCountryApi = (countryCode?: string): UseQueryResult<Country, Error> => {
  const client = useGraphQLClient(getLambdaEndpoint(brand.key))
  const normalizedCountryCode = countryCode?.toUpperCase()

  const fetchCountry = async (brand: string, countryCode: string): Promise<Country> => {
    const data = await client.request<CountryQueryResponse>(GET_COUNTRY_QUERY, {
      input: {
        brand,
        countryCode: countryCode.toUpperCase(),
      },
    })
    return data.getCountry
  }

  return useQuery<Country, Error>({
    queryKey: ['country', brand.key, normalizedCountryCode],
    enabled: !!normalizedCountryCode,
    queryFn: () => fetchCountry(brand.key, normalizedCountryCode!),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })
}

export const useCountriesApi = (): UseQueryResult<Country[], Error> => {
  const client = useGraphQLClient(getLambdaEndpoint(brand.key))

  const fetchCountries = async (): Promise<Country[]> => {
    const data = await client.request<CountriesQueryResponse>(GET_COUNTRIES_QUERY, {
      input: { brand: brand.key },
    })

    return data.getCountries
  }

  return useQuery<Country[], Error>({
    queryKey: ['countries', brand.key],
    queryFn: fetchCountries,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  })
}
